import { BRAND } from '@archetelos/ui/brand';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { computeTierDisplay } from '@/lib/pricing';
import { founderQueries } from '@/lib/queries/founder';
import { subscriptionQueries } from '@/lib/queries/subscription';
import { StripeFunctionError, startCheckout } from '@/lib/stripe-client';

interface SubscribeSearch {
  plan?: 'monthly' | 'annual';
}

export const Route = createFileRoute('/subscribe')({
  // Preselect the plan carried from the marketing membership page; absent or
  // invalid falls through to the default (annual).
  validateSearch: (search): SubscribeSearch => ({
    plan: search.plan === 'monthly' || search.plan === 'annual' ? search.plan : undefined,
  }),
  component: SubscribeRoute,
});

function SubscribeRoute() {
  const { user, loading } = useRequireAuth();
  const { plan } = Route.useSearch();
  const navigate = useNavigate();
  const founderSlots = useQuery(founderQueries.slotsRemaining());
  const currentSub = useQuery(subscriptionQueries.current(user?.id));

  const [selectedTier, setSelectedTier] = useState<'monthly' | 'annual'>(plan ?? 'annual');
  const [claimFounder, setClaimFounder] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If the user already subscribes, bounce to settings with a notice.
  useEffect(() => {
    if (!currentSub.data) return;
    if (['active', 'trialing', 'past_due'].includes(currentSub.data.status)) {
      void navigate({ to: '/subscriber/settings' });
    }
  }, [currentSub.data, navigate]);

  const slotsAvailable = (founderSlots.data ?? 0) > 0;
  const founderActuallyClaimed = claimFounder && slotsAvailable;

  const monthly = computeTierDisplay({ tier: 'monthly', isFounder: founderActuallyClaimed });
  const annual = computeTierDisplay({ tier: 'annual', isFounder: founderActuallyClaimed });

  const onCheckout = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      const baseUrl = window.location.origin;
      const url = await startCheckout({
        tier: selectedTier,
        claimFounder: founderActuallyClaimed,
        returnUrl: `${baseUrl}/subscriber/welcome?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${baseUrl}/subscribe`,
      });
      window.location.href = url;
    } catch (err) {
      if (err instanceof StripeFunctionError && err.code === 'founder_slots_exhausted') {
        toast.error('Founder slots were just claimed. You can continue at standard pricing.');
        setClaimFounder(false);
        await founderSlots.refetch();
      } else if (err instanceof StripeFunctionError && err.code === 'already_subscribed') {
        toast.error('You already have an active subscription.');
        void navigate({ to: '/subscriber/settings' });
      } else {
        toast.error(err instanceof Error ? err.message : 'Unable to start checkout');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-parchment">
      <main className="mx-auto max-w-3xl px-6 py-16">
        <header className="text-center">
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Subscribe</p>
          <h1 className="mt-2 font-serif text-3xl text-ink balance sm:text-4xl">
            A subscription supports the work and unlocks everything we publish.
          </h1>
        </header>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          <PricingCard
            label="Monthly"
            firstCycle={monthly.firstCycleDisplay}
            ongoing={monthly.ongoingDisplay}
            cycleLabel="per month"
            selected={selectedTier === 'monthly'}
            onSelect={() => setSelectedTier('monthly')}
            footnote={founderActuallyClaimed ? 'First 12 months then $6/month' : null}
          />
          <PricingCard
            label="Annual"
            firstCycle={annual.firstCycleDisplay}
            ongoing={annual.ongoingDisplay}
            cycleLabel="per year"
            selected={selectedTier === 'annual'}
            onSelect={() => setSelectedTier('annual')}
            badge="Two months free"
            preferred
            footnote={founderActuallyClaimed ? 'First year then $60/year' : null}
          />
        </div>

        <section className="mt-12 rounded-lg border border-ink/10 bg-parchment p-6">
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
            Founding Members
          </p>
          <p className="mt-3 font-sans text-base text-ink/80 pretty">
            The first 500 subscribers receive 50% off their first year and a permanent 25% discount
            on annual subscriptions.{' '}
            <span className="font-medium text-ink">
              {founderSlots.data ?? 0} slot{founderSlots.data === 1 ? '' : 's'} remaining.
            </span>
          </p>
          <label className="mt-4 flex items-start gap-3 text-sm text-ink">
            <input
              type="checkbox"
              checked={claimFounder}
              onChange={(e) => setClaimFounder(e.target.checked)}
              disabled={!slotsAvailable}
              className="mt-1 h-4 w-4 rounded border-ink/30 text-oxblood focus:ring-oxblood"
            />
            <span>
              {slotsAvailable
                ? `Claim founder status (${founderSlots.data} remaining)`
                : 'Founder slots have been claimed. Welcome anyway.'}
            </span>
          </label>
        </section>

        <button
          type="button"
          onClick={() => void onCheckout()}
          disabled={submitting}
          className="mt-10 w-full rounded-md bg-oxblood px-6 py-3 font-sans text-sm font-medium text-on-accent hover:bg-oxblood/90 disabled:opacity-60"
        >
          {submitting ? 'Starting checkout…' : 'Continue to checkout'}
        </button>

        <p className="mt-6 text-center text-xs text-ink/50">
          Cancel anytime. We don&apos;t share your data.
        </p>

        <p className="mt-12 text-center text-xs uppercase tracking-[0.12em] text-ink/40">
          {BRAND.name}
        </p>
      </main>
    </div>
  );
}

interface PricingCardProps {
  label: string;
  firstCycle: string;
  ongoing: string;
  cycleLabel: string;
  selected: boolean;
  onSelect: () => void;
  badge?: string;
  preferred?: boolean;
  footnote: string | null;
}

function PricingCard({
  label,
  firstCycle,
  ongoing,
  cycleLabel,
  selected,
  onSelect,
  badge,
  preferred,
  footnote,
}: PricingCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`text-left rounded-lg border bg-parchment p-6 transition-colors ${
        selected
          ? 'border-oxblood'
          : preferred
            ? 'border-ink/30 hover:border-oxblood/60'
            : 'border-ink/15 hover:border-oxblood/60'
      } ${preferred ? 'border-2' : ''}`}
    >
      <div className="flex items-center justify-between">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">{label}</p>
        {badge && (
          <span className="rounded-sm bg-slate/15 px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] text-ink/60">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-4 font-serif text-3xl text-ink">
        {firstCycle}
        <span className="ml-1 font-sans text-sm text-ink/60">{cycleLabel}</span>
      </p>
      {firstCycle !== ongoing && (
        <p className="mt-1 font-sans text-xs text-ink/50">
          Then {ongoing} {cycleLabel}
        </p>
      )}
      {footnote && <p className="mt-3 font-sans text-xs text-ink/60">{footnote}</p>}
    </button>
  );
}
